import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comicDetail, comicDetail2 } from '@/mocks/comicData';

const allDetails: Record<string, typeof comicDetail> = {
  'comic-1': comicDetail,
  'comic-2': comicDetail2,
};

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const comic = useMemo(() => {
    if (!id) return null;
    return allDetails[id] || comicDetail;
  }, [id]);

  if (!comic) return null;

  const statusLabel = comic.status === 'ongoing' ? t('comic.status.ongoing') : t('comic.status.completed');
  const canonicalUrl = `http://szshops.com/comic/${comic.id}`;
  const pageTitle = `${comic.title} - 韩国漫画在线免费阅读_韩漫大全`;

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${comic.title} - 韩国漫画`,
    "description": comic.description,
    "url": canonicalUrl,
    "inLanguage": "zh-CN",
    "author": { "@type": "Person", "name": comic.author },
    "datePublished": "2020-01-01",
    "dateModified": comic.lastUpdate,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://szshops.com/" },
        { "@type": "ListItem", "position": 2, "name": "分类", "item": "http://szshops.com/category/all" },
        { "@type": "ListItem", "position": 3, "name": comic.title }
      ]
    }
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={`${comic.title}（${comic.titleKo}），${comic.genres.join('、')}题材韩国漫画，评分${comic.rating}，${comic.reads}阅读。韩漫大全提供${comic.title}在线免费阅读，最新章节持续更新。`} />
      <link rel="canonical" href={canonicalUrl} />
      <script type="application/ld+json">{JSON.stringify(ldJson)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="pt-20">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.category'), to: '/category/all' },
                { label: comic.title },
              ]}
            />

            <section className="relative w-full h-[400px] md:h-[480px] flex items-end overflow-hidden">
              <img
                src={comic.heroImage}
                alt={`${comic.title} - 韩漫封面`}
                title={`${comic.title} - 韩国漫画在线免费阅读`}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/30 to-foreground-950/20"></div>
              <div className="relative z-10 w-full px-6 md:px-10 pb-10 md:pb-14 max-w-[1280px] mx-auto">
                <div className="flex flex-col md:flex-row md:items-end gap-6">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫大全`}
                    className="w-32 h-44 md:w-40 md:h-56 rounded-lg object-cover object-top ring-4 ring-background-50/20"
                  />
                  <div className="flex-1">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                      comic.status === 'ongoing'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-foreground-500 text-background-50'
                    }`}>
                      {statusLabel}
                    </span>
                    <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-2">
                      {comic.title}
                    </h1>
                    <p className="text-sm text-background-50/60 mb-3">{comic.titleKo}</p>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-background-50/70">
                      <span>{t('comic.info.author')}: {comic.author}</span>
                      <span className="w-1 h-1 rounded-full bg-background-50/30"></span>
                      <span>{t('comic.info.artist')}: {comic.artist}</span>
                      <span className="w-1 h-1 rounded-full bg-background-50/30"></span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        {comic.rating}
                      </span>
                    </div>
                  </div>
                  <Link
                    to={`/comic/${comic.id}/read/1`}
                    className="whitespace-nowrap px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer self-start md:self-end text-center"
                  >
                    {t('comic.cta.btn')}
                  </Link>
                </div>
              </div>
            </section>

            <section className="bg-background-100 py-12 md:py-16">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
                  <div className="bg-foreground-950 rounded-xl p-6 md:p-8 text-white lg:col-span-1">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-background-50/15 text-background-50 mb-4">
                      <i className="ri-information-line"></i>
                    </span>
                    <h3 className="text-lg font-heading font-bold text-background-50 mb-2">{t('comic.desc')}</h3>
                    <p className="text-sm text-background-50/65 leading-relaxed">{comic.description}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {comic.genres.map((g) => (
                        <span key={g} className="px-2.5 py-1 rounded-full text-xs bg-background-50/10 text-background-50/70 border border-background-50/15">
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 md:p-8 border border-background-200/70 lg:col-span-1">
                    <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">作品信息</h3>
                    <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                      <div>
                        <p className="text-xs text-foreground-500">{t('comic.info.rating')}</p>
                        <p className="text-lg font-bold text-foreground-950">{comic.rating}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">{t('comic.info.reads')}</p>
                        <p className="text-lg font-bold text-foreground-950">{comic.reads}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">{t('comic.info.chapters')}</p>
                        <p className="text-lg font-bold text-foreground-950">{comic.chapterCount}话</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">{t('comic.info.lastUpdate')}</p>
                        <p className="text-lg font-bold text-foreground-950">{comic.lastUpdate}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 md:p-8 border border-background-200/70 lg:col-span-1">
                    <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">{t('comic.chapters.title')}</h3>
                    <div className="flex flex-col gap-3 max-h-[280px] overflow-y-auto">
                      {comic.chapters.map((ch) => (
                        <Link
                          key={ch.num}
                          to={`/comic/${comic.id}/read/${ch.num}`}
                          className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-background-100 transition-colors group cursor-pointer"
                        >
                          <div className="flex items-center gap-2.5">
                            <span className="text-xs font-medium text-foreground-400 w-10">第{ch.num}话</span>
                            <span className="text-sm text-foreground-800 group-hover:text-primary-500 transition-colors">
                              {ch.title}
                            </span>
                            {ch.isNew && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-100 text-primary-600">NEW</span>
                            )}
                          </div>
                          <span className="text-xs text-foreground-400">{ch.date}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-12 md:py-16">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto">
                <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">{t('comic.reviews.title')}</h3>
                <div className="space-y-5">
                  {comic.reviews.map((review) => (
                    <div key={review.id} className="flex gap-4 p-5 bg-background-100 rounded-xl">
                      <img
                        src={review.avatar}
                        alt={review.author}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-sm font-semibold text-foreground-950">{review.author}</span>
                          <div className="flex">
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <i key={i} className="ri-star-fill text-accent-500 text-[10px]"></i>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-2">{review.text}</p>
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span>{review.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-thumb-up-line"></i>
                            {review.likes}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-12 md:py-16 bg-background-50 text-center">
              <div className="px-6 md:px-10 max-w-[1280px] mx-auto">
                <div className="relative rounded-2xl overflow-hidden mb-8 md:mb-10 mx-auto max-w-2xl">
                  <img
                    src={comic.heroImage}
                    alt={comic.title}
                    className="w-full h-[200px] md:h-[260px] object-cover"
                    loading="lazy"
                  />
                </div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                  {t('comic.cta.line1')}
                </h2>
                <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500 italic mb-2">
                  {t('comic.cta.line2')}
                </p>
                <p className="text-base text-foreground-500 mb-8">{t('comic.cta.line3')}</p>
                <Link
                  to={`/comic/${comic.id}/read/1`}
                  className="inline-block whitespace-nowrap px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  {t('comic.cta.btn')}
                </Link>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}